import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, Plus, X } from "lucide-react";

const ABILITIES = ["str", "dex", "con", "int", "wis", "cha"];
const FIELD_TYPES = ["text", "number", "checkbox", "dropdown", "textarea"];
const BLOCK_LABELS = {
  ability_scores: "Ability Scores", saves: "Saving Throws", ac_hp: "AC & Hit Points",
  skills: "Skills", spellcasting: "Spellcasting", custom_fields: "Custom Fields",
};

export default function HomebrewRulesetEditor({ gameId, ruleset, onSave }) {
  const [modules, setModules] = useState([]);
  const [draft, setDraft] = useState(ruleset);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/games/${gameId}/homebrew/modules`).then((r) => setModules(r.data));
  }, [gameId]);

  useEffect(() => setDraft(ruleset), [ruleset]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/games/${gameId}`, { homebrew_ruleset: draft });
      onSave(data);
      toast.success("Homebrew ruleset saved");
    } catch {
      toast.error("Could not save ruleset");
    } finally {
      setSaving(false);
    }
  };

  const toggleModule = (id) => setDraft((d) => ({ ...d, rule_modules: { ...d.rule_modules, [id]: !d.rule_modules[id] } }));

  const moveBlock = (index, dir) => {
    const layout = [...draft.sheet_layout];
    const target = index + dir;
    if (target < 0 || target >= layout.length) return;
    [layout[index], layout[target]] = [layout[target], layout[index]];
    setDraft((d) => ({ ...d, sheet_layout: layout }));
  };

  const skillRows = draft.custom_skills || [];
  const setSkillRows = (rows) => setDraft((d) => ({ ...d, custom_skills: rows.length ? rows : null }));

  const fieldRows = draft.custom_fields || [];
  const setFieldRows = (rows) => setDraft((d) => ({ ...d, custom_fields: rows }));

  return (
    <div className="space-y-4" data-testid="homebrew-ruleset-editor">
      <p className="text-[10px] uppercase text-[#f1e0c5]/40">Rule Modules</p>
      <div className="space-y-2">
        {modules.map((m) => (
          <label key={m.id} className="flex items-start gap-2.5 text-xs text-[#f1e0c5]/70 cursor-pointer">
            <Checkbox data-testid={`homebrew-module-${m.id}`} checked={!!draft.rule_modules?.[m.id]} onCheckedChange={() => toggleModule(m.id)} className="mt-0.5" />
            <span>
              <span className="text-[#f1e0c5]">{m.label}</span>
              <span className="block text-[#f1e0c5]/30 text-[10px]">{m.description}</span>
            </span>
          </label>
        ))}
      </div>

      {draft.rule_modules?.skills && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase text-[#f1e0c5]/40">Custom Skill List (leave empty to use the default 16)</p>
          {skillRows.map((row, i) => (
            <div key={i} className="flex gap-1.5" data-testid={`homebrew-skill-row-${i}`}>
              <Input
                data-testid={`homebrew-skill-name-${i}`}
                value={row.name}
                onChange={(e) => setSkillRows(skillRows.map((r, ix) => (ix === i ? { ...r, name: e.target.value } : r)))}
                placeholder="Skill name"
                className="bg-black/30 border-white/10 h-8 text-xs flex-1"
              />
              <Select value={row.ability} onValueChange={(v) => setSkillRows(skillRows.map((r, ix) => (ix === i ? { ...r, ability: v } : r)))}>
                <SelectTrigger data-testid={`homebrew-skill-ability-${i}`} className="bg-black/30 border-white/10 h-8 w-20 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>{ABILITIES.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
              <button data-testid={`homebrew-skill-remove-${i}`} onClick={() => setSkillRows(skillRows.filter((_, ix) => ix !== i))} className="text-[#f1e0c5]/40 hover:text-[#D90429]">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
          <Button data-testid="homebrew-skill-add" size="sm" variant="secondary" className="w-full rounded-full gap-1.5" onClick={() => setSkillRows([...skillRows, { name: "", ability: "str" }])}>
            <Plus className="w-3.5 h-3.5" /> Add Skill
          </Button>
        </div>
      )}

      <div className="space-y-2">
        <p className="text-[10px] uppercase text-[#f1e0c5]/40">Custom Fields (for anything your homebrew needs that isn't covered above)</p>
        {fieldRows.map((row, i) => (
          <div key={row.id} className="bg-black/20 border border-white/10 rounded-lg p-2 space-y-1.5" data-testid={`homebrew-field-row-${i}`}>
            <div className="flex gap-1.5">
              <Input
                data-testid={`homebrew-field-label-${i}`}
                value={row.label}
                onChange={(e) => setFieldRows(fieldRows.map((r, ix) => (ix === i ? { ...r, label: e.target.value } : r)))}
                placeholder="Field label (e.g. Mana)"
                className="bg-black/30 border-white/10 h-8 text-xs flex-1"
              />
              <button data-testid={`homebrew-field-remove-${i}`} onClick={() => setFieldRows(fieldRows.filter((_, ix) => ix !== i))} className="text-[#f1e0c5]/40 hover:text-[#D90429]">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="flex gap-1.5 items-center">
              <Select value={row.type} onValueChange={(v) => setFieldRows(fieldRows.map((r, ix) => (ix === i ? { ...r, type: v } : r)))}>
                <SelectTrigger data-testid={`homebrew-field-type-${i}`} className="bg-black/30 border-white/10 h-8 text-xs flex-1"><SelectValue /></SelectTrigger>
                <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              {row.type === "number" && (
                <label className="flex items-center gap-1.5 text-[10px] text-[#f1e0c5]/60 shrink-0">
                  <Checkbox data-testid={`homebrew-field-rollable-${i}`} checked={!!row.rollable} onCheckedChange={(v) => setFieldRows(fieldRows.map((r, ix) => (ix === i ? { ...r, rollable: v } : r)))} />
                  Rollable
                </label>
              )}
            </div>
            {row.type === "dropdown" && (
              <Input
                data-testid={`homebrew-field-options-${i}`}
                value={row.options || ""}
                onChange={(e) => setFieldRows(fieldRows.map((r, ix) => (ix === i ? { ...r, options: e.target.value } : r)))}
                placeholder="Comma-separated options"
                className="bg-black/30 border-white/10 h-8 text-xs"
              />
            )}
          </div>
        ))}
        <Button
          data-testid="homebrew-field-add"
          size="sm"
          variant="secondary"
          className="w-full rounded-full gap-1.5"
          onClick={() => setFieldRows([...fieldRows, { id: crypto.randomUUID(), label: "", type: "text", rollable: false, options: "" }])}
        >
          <Plus className="w-3.5 h-3.5" /> Add Custom Field
        </Button>
      </div>

      <div className="space-y-2">
        <p className="text-[10px] uppercase text-[#f1e0c5]/40">Sheet Layout (order shown to players)</p>
        {draft.sheet_layout?.map((blockId, i) => (
          <div key={blockId} data-testid={`homebrew-layout-row-${blockId}`} className="flex items-center justify-between bg-black/20 border border-white/10 rounded-lg px-3 py-1.5 text-xs">
            <span>{BLOCK_LABELS[blockId] || blockId}</span>
            <div className="flex gap-1">
              <button data-testid={`homebrew-layout-up-${blockId}`} onClick={() => moveBlock(i, -1)} disabled={i === 0} className="text-[#f1e0c5]/40 hover:text-[#D4AF37] disabled:opacity-20">
                <ChevronUp className="w-3.5 h-3.5" />
              </button>
              <button data-testid={`homebrew-layout-down-${blockId}`} onClick={() => moveBlock(i, 1)} disabled={i === draft.sheet_layout.length - 1} className="text-[#f1e0c5]/40 hover:text-[#D4AF37] disabled:opacity-20">
                <ChevronDown className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <Button data-testid="homebrew-ruleset-save" onClick={save} disabled={saving} className="w-full rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-xs">
        {saving ? "Saving…" : "Save Homebrew Ruleset"}
      </Button>
    </div>
  );
}
